/**
 * Which checkpoints the tournament dialog's bar shows. All checkpoints are cut
 * into separate groups of `sector_size` in a row (13 by 3: [0 1 2] [3 4 5] [6 7 8]
 * [9 10 11] [12]); the bar shows the current group and swaps it for the next one
 * whole once its last checkpoint is reached, staying on the last group to the end.
 * The grand prize chest at the bar's right end is not a checkpoint.
 * No server or UI imports: tests run it in node.
 */

export const DEFAULT_SECTOR_SIZE = 3;
export const MIN_SECTOR_SIZE = 2;
export const MAX_SECTOR_SIZE = 5;

/** Admin value → 2..5; missing or junk falls back to 3. */
export function normalizeSectorSize( raw: unknown ): number
{
  const value = typeof raw === "string" ? Number( raw ) : raw;
  if ( typeof value !== "number" || !Number.isFinite( value ) ) return DEFAULT_SECTOR_SIZE;
  return Math.min( MAX_SECTOR_SIZE, Math.max( MIN_SECTOR_SIZE, Math.round( value ) ) );
}

export interface ProgressSector
{
  /** First and last checkpoint index of the group on screen, inclusive; `last < first` with no checkpoints. */
  first: number;
  last: number;
}

/** `reached` = how many checkpoints from the start the player has reached (crowns only grow, so they are a prefix). */
export function progressSector( total: number, reached: number, sector_size: number ): ProgressSector
{
  const size = normalizeSectorSize( sector_size );
  if ( total <= 0 ) return { first: 0, last: -1 };

  const last_group = Math.floor( ( total - 1 ) / size );
  const group = Math.min( last_group, Math.floor( Math.max( 0, reached ) / size ) );
  const first = group * size;
  return { first, last: Math.min( first + size - 1, total - 1 ) };
}

/** How many leading thresholds the crowns have passed. */
export function reachedCount( thresholds: readonly number[], crowns: number ): number
{
  let count = 0;
  while ( count < thresholds.length && crowns >= thresholds[count] ) count++;
  return count;
}

/**
 * Bar fill along fixed points: `xs[0]` is the left icon, then the sector's
 * checkpoints. The left icon stands for the checkpoint
 * before the sector (0 crowns for the first one); between two points the end
 * moves with the crowns. Returns an x on the same axis as `xs`.
 */
export function fillX( xs: readonly number[], values: readonly number[], crowns: number ): number
{
  if ( xs.length === 0 ) return 0;
  if ( crowns <= values[0] ) return xs[0];
  for ( let i = 1; i < xs.length; i++ )
  {
    if ( crowns < values[i] )
      return xs[i - 1] + ( xs[i] - xs[i - 1] ) * ( crowns - values[i - 1] ) / Math.max( 1, values[i] - values[i - 1] );
  }
  return xs[xs.length - 1];
}
